import { z } from 'zod';
import { CliError, CliErrorCode } from '@wix/cli-error';
import {
  readJson,
  pathExists,
  readPackageJson,
  writePackageJson,
} from '@wix/cli-fs';
import { gt, minVersion } from 'semver';
import type {
  DependenciesUpdaterOptions,
  MinimumDependencies,
} from './types.js';

type PackageJson = Awaited<ReturnType<typeof readPackageJson>>;
type DependencySection = 'dependencies' | 'devDependencies';

const dependenciesJsonSchema = z.object({
  dependencies: z.record(z.string()).optional(),
  devDependencies: z.record(z.string()).optional(),
});

async function readDependenciesJson(filePath: string) {
  const json = await readJson(filePath);
  const parsedJson = await dependenciesJsonSchema.safeParseAsync(json);

  if (!parsedJson.success) {
    throw new CliError({
      code: CliErrorCode.InvalidDependenciesJsonSchemaError({
        path: filePath,
        zodError: parsedJson.error,
      }),
      cause: parsedJson.error,
    });
  }

  return parsedJson.data;
}

function getPackageJsonDependency(
  packageJson: PackageJson,
  dependencyPackageName: string
) {
  if (packageJson.dependencies?.[dependencyPackageName]) {
    return {
      section: 'dependencies' as const,
      version: packageJson.dependencies[dependencyPackageName],
    };
  }

  if (packageJson.devDependencies?.[dependencyPackageName]) {
    return {
      section: 'devDependencies' as const,
      version: packageJson.devDependencies[dependencyPackageName],
    };
  }
}

function setPackageJsonDependency(
  packageJson: PackageJson,
  section: DependencySection,
  dependencyPackageName: string,
  version: string
) {
  if (!packageJson[section]) {
    packageJson[section] = {};
  }

  packageJson[section][dependencyPackageName] = version;
}

function shouldBumpDependencyVersion(
  currentVersionRange: string,
  nextVersionRange: string
) {
  let currentMinimumVersion;
  let nextMinimumVersion;

  try {
    currentMinimumVersion = minVersion(currentVersionRange);
    nextMinimumVersion = minVersion(nextVersionRange);
  } catch {
    return false;
  }

  if (!currentMinimumVersion || !nextMinimumVersion) {
    return false;
  }

  return gt(nextMinimumVersion, currentMinimumVersion);
}

function syncDependencies(
  packageJson: PackageJson,
  section: DependencySection,
  dependencies: Record<string, string> | undefined,
  updatedDependencies: Set<string>
) {
  if (!dependencies) {
    return;
  }

  for (const [dependencyPackageName, version] of Object.entries(dependencies)) {
    const existingDependency = getPackageJsonDependency(
      packageJson,
      dependencyPackageName
    );

    if (!existingDependency) {
      setPackageJsonDependency(
        packageJson,
        section,
        dependencyPackageName,
        version
      );
      updatedDependencies.add(dependencyPackageName);
      continue;
    }

    if (shouldBumpDependencyVersion(existingDependency.version, version)) {
      setPackageJsonDependency(
        packageJson,
        existingDependency.section,
        dependencyPackageName,
        version
      );
      updatedDependencies.add(dependencyPackageName);
    }
  }
}

export function ensureMinimumDependencies(
  packageJson: PackageJson,
  dependencies: MinimumDependencies
) {
  const updatedDependencies = new Set<string>();

  syncDependencies(
    packageJson,
    'dependencies',
    dependencies.dependencies,
    updatedDependencies
  );
  syncDependencies(
    packageJson,
    'devDependencies',
    dependencies.devDependencies,
    updatedDependencies
  );

  return [...updatedDependencies];
}

export async function dependenciesUpdater(opts: DependenciesUpdaterOptions) {
  const { projectFolder, dependenciesJsonPath, dynamicPackages } = opts;
  const updatedDependencies = new Set<string>();

  const hasDependenciesJson = await pathExists(dependenciesJsonPath);

  if (!hasDependenciesJson && !dynamicPackages) {
    return [];
  }

  const dependenciesConfig: MinimumDependencies = hasDependenciesJson
    ? await readDependenciesJson(dependenciesJsonPath)
    : {};
  const packageJson = await readPackageJson(projectFolder);

  syncDependencies(
    packageJson,
    'dependencies',
    dependenciesConfig.dependencies,
    updatedDependencies
  );
  syncDependencies(
    packageJson,
    'dependencies',
    dynamicPackages,
    updatedDependencies
  );
  syncDependencies(
    packageJson,
    'devDependencies',
    dependenciesConfig.devDependencies,
    updatedDependencies
  );

  if (updatedDependencies.size) {
    await writePackageJson(projectFolder, packageJson);
  }

  return [...updatedDependencies];
}
